import "@/App.css";
import "@/lib/i18n";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { BrandingProvider } from "@/contexts/BrandingContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppLayout from "@/components/AppLayout";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";

// Keep eager: top-level public + auth shell (small, on every load)
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import AuthCallback from "@/pages/AuthCallback";
import PublicEnquiry from "@/pages/PublicEnquiry";
import DemoEnquiry from "@/pages/DemoEnquiry";
import SetupPassword from "@/pages/SetupPassword";
import Welcome from "@/pages/Welcome";
import AdmissionForm from "@/pages/AdmissionForm";
import CompliancePage from "@/pages/CompliancePage";

// Keep eager: small dashboard shells used at first login per role
import AdminDashboard from "@/pages/dashboards/AdminDashboard";
import HqDashboard from "@/pages/dashboards/HqDashboard";
import FranchiseHqDashboard from "@/pages/dashboards/FranchiseHqDashboard";
import BranchDashboard from "@/pages/dashboards/BranchDashboard";
import TeacherDashboard from "@/pages/dashboards/TeacherDashboard";
import ParentDashboard from "@/pages/dashboards/ParentDashboard";
import CounsellorDashboard from "@/pages/dashboards/CounsellorDashboard";
import TechCoordDashboard from "@/pages/dashboards/TechCoordDashboard";
import MedicalCoordDashboard from "@/pages/dashboards/MedicalCoordDashboard";
import {
  TransportStaffDashboard, InventoryStaffDashboard,
  SupportStaffDashboard, VendorDashboard,
} from "@/pages/dashboards/SpecialisedStaffDashboards";
import TechSupport from "@/pages/TechSupport";
import Documents from "@/pages/Documents";
import DocumentsConfig from "@/pages/DocumentsConfig";

// Lightweight common modules — kept eager for snappy navigation
import Branches from "@/pages/modules/Branches";
import Enquiries from "@/pages/modules/Enquiries";
import Admissions from "@/pages/modules/Admissions";
import Students from "@/pages/modules/Students";
import StudentDetail from "@/pages/modules/StudentDetail";
import Teachers from "@/pages/modules/Teachers";
import TeacherDetail from "@/pages/modules/TeacherDetail";
import Parents from "@/pages/modules/Parents";
import Fees from "@/pages/modules/Fees";
import Attendance from "@/pages/modules/Attendance";
import DailyUpdates from "@/pages/modules/DailyUpdates";
import Pickup from "@/pages/modules/Pickup";
import Apparel from "@/pages/modules/Apparel";
import Communications from "@/pages/modules/Chat";
import Health from "@/pages/modules/Health";
import Meals from "@/pages/modules/Meals";
import Settings from "@/pages/modules/Settings";
import Notifications from "@/pages/modules/Notifications";
import TeacherTasks from "@/pages/modules/TeacherTasks";
import StaffDashboard from "@/pages/dashboards/StaffDashboard";

// Heavy / less-frequented routes — lazy-load via dynamic import.
// Webpack/CRA splits these into separate chunks so the initial bundle stays slim.
const Curriculum = lazy(() => import("@/pages/modules/Curriculum"));
const DigiBoard = lazy(() => import("@/pages/modules/DigiBoard"));
const Events = lazy(() => import("@/pages/modules/Events"));
const Marketing = lazy(() => import("@/pages/modules/Marketing"));
const Franchise = lazy(() => import("@/pages/modules/Franchise"));
const AIWorkspace = lazy(() => import("@/pages/modules/AIWorkspace"));
const Reports = lazy(() => import("@/pages/modules/Reports"));
const EnquiryAnalytics = lazy(() => import("@/pages/modules/EnquiryAnalytics"));
const SystemStatus = lazy(() => import("@/pages/modules/SystemStatus"));
const HRMS = lazy(() => import("@/pages/modules/HRMS"));
const Vendors = lazy(() => import("@/pages/modules/Vendors"));
const Activities = lazy(() => import("@/pages/modules/Activities"));
const AdminBot = lazy(() => import("@/pages/modules/AdminBot"));
const PhotoStudio = lazy(() => import("@/pages/modules/PhotoStudio"));
const LMS = lazy(() => import("@/pages/modules/LMS"));
const LessonDetail = lazy(() => import("@/pages/modules/LessonDetail"));
const WeeklyDigest = lazy(() => import("@/pages/modules/WeeklyDigest"));
const Behaviour = lazy(() => import("@/pages/modules/Behaviour"));
const Vaccinations = lazy(() => import("@/pages/modules/Vaccinations"));
const HealthRecords = lazy(() => import("@/pages/modules/HealthRecords"));
const Payroll = lazy(() => import("@/pages/modules/Payroll"));
const SalaryStructures = lazy(() => import("@/pages/modules/SalaryStructures"));
const PayrollHQSummary = lazy(() => import("@/pages/modules/PayrollHQSummary"));
const PayrollCalendar = lazy(() => import("@/pages/modules/PayrollCalendar"));
const PayrollSchedule = lazy(() => import("@/pages/modules/PayrollSchedule"));
const KYCReview = lazy(() => import("@/pages/modules/KYCReview"));
const Refunds = lazy(() => import("@/pages/modules/Refunds"));
const WebsiteAnalytics = lazy(() => import("@/pages/modules/WebsiteAnalytics"));
const VerificationQueue = lazy(() => import("@/pages/modules/VerificationQueue"));
const WhatsAppDiag = lazy(() => import("@/pages/modules/WhatsAppDiag"));
const VerificationDemo = lazy(() => import("@/pages/modules/VerificationDemo"));
const AuditorDashboard = lazy(() => import("@/pages/dashboards/AuditorDashboard"));
const BrandingSettings = lazy(() => import("@/pages/modules/BrandingSettings"));
const EntryExit = lazy(() => import("@/pages/modules/EntryExit"));
const ParentEntryExit = lazy(() => import("@/pages/parent/ParentEntryExit"));
const GatePass = lazy(() => import("@/pages/modules/GatePass"));
const AuthorisedContacts = lazy(() => import("@/pages/modules/AuthorisedContacts"));
const VerifyDocument = lazy(() => import("@/pages/VerifyDocument"));
const VerifyCertificate = lazy(() => import("@/pages/VerifyCertificate"));
const UserManagement = lazy(() => import("@/pages/admin/UserManagement"));
const SerialFormatsAndQR = lazy(() => import("@/pages/admin/SerialFormatsAndQR"));
const FeatureToggles = lazy(() => import("@/pages/admin/FeatureToggles"));
const FinanceV2 = lazy(() => import("@/pages/modules/Finance"));
const PublicFormModify = lazy(() => import("@/pages/PublicFormModify"));
const ProfileSecurity = lazy(() => import("@/pages/ProfileSecurity"));
const ParentLearning = lazy(() => import("@/pages/parent/ParentLearning"));
const TeacherLMS = lazy(() => import("@/pages/teacher/TeacherLMS"));
const AdminLMS = lazy(() => import("@/pages/modules/AdminLMS"));
const CertificateView = lazy(() => import("@/pages/CertificateView"));
import RequireRole from "@/components/RequireRole";

import ParentTimeline from "@/pages/parent/ParentTimeline";
import ParentAttendance from "@/pages/parent/ParentAttendance";
import ParentFees from "@/pages/parent/ParentFees";
import ParentApparel from "@/pages/parent/ParentApparel";
import ParentEvents from "@/pages/parent/ParentEvents";
import ParentMeals from "@/pages/parent/ParentMeals";
import ParentNotices from "@/pages/parent/ParentNotices";
import ParentReferAFriend from "@/pages/parent/ParentReferAFriend";
import ParentSettings from "@/pages/parent/ParentSettings";
import StaffSettings from "@/pages/admin/StaffSettings";

import { homeForUser } from "@/lib/api";

function HomeRedirect() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={homeForUser(user)} replace />;
}

function GlobalRouter() {
  // Handle Emergent Auth callback (URL hash) BEFORE other routing
  const location = useLocation();
  if (typeof window !== "undefined" && (location.hash?.includes("session_id=") || window.location.hash?.includes("session_id="))) {
    return <AuthCallback />;
  }
  return (
    <Suspense fallback={<RouteLoader />}>
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/enquiry" element={<PublicEnquiry />} />
      <Route path="/demo" element={<DemoEnquiry />} />
      <Route path="/setup-password" element={<SetupPassword />} />
      <Route path="/admission" element={<AdmissionForm />} />
      <Route path="/modify" element={<PublicFormModify />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/verify/cert/:certNo" element={<VerifyCertificate />} />
      <Route path="/verify/:documentId" element={<VerifyDocument />} />
      <Route path="/legal/terms" element={<CompliancePage slug="terms" />} />
      <Route path="/legal/privacy" element={<CompliancePage slug="privacy" />} />
      <Route path="/legal/refund" element={<CompliancePage slug="refund" />} />
      <Route path="/legal/child-privacy" element={<CompliancePage slug="child-privacy" />} />

      {/* Post-login welcome splash — protected (auth required) but rendered
          full-screen WITHOUT the app sidebar/header for a polished first-frame. */}
      <Route path="/app/welcome" element={<ProtectedRoute><Welcome /></ProtectedRoute>} />

      <Route path="/app" element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
        <Route index element={<HomeRedirect />} />

        {/* Role landings */}
        <Route path="admin" element={<AdminDashboard />} />
        <Route path="hq" element={<HqDashboard />} />
        <Route path="franchise-hq" element={<FranchiseHqDashboard />} />
        <Route path="branch" element={<BranchDashboard />} />
        <Route path="teacher" element={<TeacherDashboard />} />
        <Route path="parent" element={<ParentDashboard />} />
        <Route path="counsellor" element={<CounsellorDashboard />} />
        <Route path="staff" element={<StaffDashboard />} />
        {/* Friendly aliases — "My HR / My Payslips" surfaces the same
            self-service hub under intuitive URLs every staff role can
            reach from the sidebar. */}
        <Route path="my-hr" element={<StaffDashboard />} />
        <Route path="my-payslips" element={<StaffDashboard />} />
        <Route path="tech-coord" element={<TechCoordDashboard />} />
        <Route path="tech-support" element={<TechSupport />} />
        <Route path="documents" element={<Documents />} />
        <Route path="documents-config" element={
          <RequireRole roles={["super_admin", "hq_admin", "franchise_hq_admin", "branch_admin", "principal"]}>
            <DocumentsConfig />
          </RequireRole>
        } />
        <Route path="transport-staff" element={<TransportStaffDashboard />} />
        <Route path="inventory-staff" element={<InventoryStaffDashboard />} />
        <Route path="support-staff" element={<SupportStaffDashboard />} />
        <Route path="vendor-portal" element={<VendorDashboard />} />
        <Route path="medical-coord" element={<MedicalCoordDashboard />} />
        <Route path="operations" element={<Pickup />} />

        {/* Shared module pages */}
        <Route path="branches" element={<Branches />} />
        <Route path="enquiries" element={<Enquiries />} />
        <Route path="admissions" element={<Admissions />} />
        <Route path="students" element={<Students />} />
        <Route path="students/:studentId" element={<StudentDetail />} />
        <Route path="teachers" element={<Teachers />} />
        <Route path="teachers/:teacherId" element={<TeacherDetail />} />
        <Route path="parents" element={<Parents />} />
        <Route path="fees" element={<Fees />} />
        <Route path="attendance" element={<Attendance />} />
        <Route path="daily-updates" element={<DailyUpdates />} />
        <Route path="pickup" element={<Pickup />} />
        <Route path="apparel" element={<Apparel />} />
        <Route path="vendors" element={<Vendors />} />
        <Route path="activities" element={<Activities />} />
        <Route path="hrms" element={<HRMS />} />
        <Route path="payroll" element={<Payroll />} />
        <Route path="payroll/salary-structures" element={<SalaryStructures />} />
        <Route path="payroll/hq-summary" element={<PayrollHQSummary />} />
        <Route path="payroll/calendar" element={<PayrollCalendar />} />
        <Route path="payroll/schedule" element={<PayrollSchedule />} />
        <Route path="hrms/kyc-review" element={<KYCReview />} />
        <Route path="finance/refunds" element={<Refunds />} />
        <Route path="admin/website-analytics" element={<WebsiteAnalytics />} />
        <Route path="admin/verification-queue" element={
          <RequireRole roles={["super_admin", "hq_admin", "franchise_hq_admin", "branch_admin", "principal", "counsellor", "academic_coordinator"]}>
            <VerificationQueue />
          </RequireRole>
        } />
        <Route path="admin/whatsapp-diag" element={
          <RequireRole roles={["super_admin"]}>
            <WhatsAppDiag />
          </RequireRole>
        } />
        <Route path="admin/verification-demo" element={
          <RequireRole roles={["super_admin", "hq_admin", "franchise_hq_admin", "branch_admin", "principal"]}>
            <VerificationDemo />
          </RequireRole>
        } />
        <Route path="admin-bot" element={<AdminBot />} />
        <Route path="photos" element={<PhotoStudio />} />
        <Route path="lms" element={<LMS />} />
        <Route path="lms/:lessonId" element={<LessonDetail />} />
        <Route path="my-tasks" element={<TeacherTasks />} />
        <Route path="digest" element={<WeeklyDigest />} />
        <Route path="parent-digests" element={<WeeklyDigest />} />
        <Route path="behaviour" element={<Behaviour />} />
        <Route path="vaccinations" element={<Vaccinations />} />
        <Route path="health" element={<HealthRecords />} />
        <Route path="auditor" element={<AuditorDashboard />} />
        <Route path="branding" element={<BrandingSettings />} />
        <Route path="users" element={
          <RequireRole roles={["super_admin", "hq_admin", "franchise_hq_admin", "branch_admin", "principal", "academic_coordinator"]}>
            <UserManagement />
          </RequireRole>
        } />
        <Route path="serial-and-qr" element={
          <RequireRole roles={["super_admin", "hq_admin", "franchise_hq_admin", "branch_admin", "principal", "non_teaching_staff"]}>
            <SerialFormatsAndQR />
          </RequireRole>
        } />
        <Route path="feature-toggles" element={
          <RequireRole roles={["super_admin", "hq_admin", "franchise_hq_admin", "branch_admin", "centre_head", "principal"]}>
            <FeatureToggles />
          </RequireRole>
        } />
        <Route path="finance-ops" element={
          <RequireRole roles={["super_admin", "hq_admin", "franchise_hq_admin", "branch_admin", "centre_head", "principal", "auditor"]}>
            <FinanceV2 />
          </RequireRole>
        } />
        <Route path="entry-exit" element={<EntryExit />} />
        <Route path="parent-entry-exit" element={<ParentEntryExit />} />
        <Route path="gate-pass" element={<GatePass />} />
        <Route path="authorised-contacts" element={<AuthorisedContacts />} />
        <Route path="curriculum" element={<Curriculum />} />
        <Route path="digiboard" element={<DigiBoard />} />
        <Route path="events" element={<Events />} />
        <Route path="communications" element={<Communications />} />
        <Route path="health" element={<Health />} />
        <Route path="meals" element={<Meals />} />
        <Route path="marketing" element={<Marketing />} />
        <Route path="franchise" element={<Franchise />} />
        <Route path="ai" element={<AIWorkspace />} />
        <Route path="reports" element={<Reports />} />
        <Route path="enquiry-analytics" element={<EnquiryAnalytics />} />
        <Route path="system-status" element={<SystemStatus />} />
        <Route path="settings" element={<Settings />} />
        <Route path="notifications" element={<Notifications />} />

        {/* Parent flows */}
        <Route path="timeline" element={<ParentTimeline />} />
        <Route path="parent-attendance" element={<ParentAttendance />} />
        <Route path="parent-fees" element={<ParentFees />} />
        <Route path="parent-apparel" element={<ParentApparel />} />
        <Route path="parent-events" element={<ParentEvents />} />
        <Route path="parent-meals" element={<ParentMeals />} />
        <Route path="parent-notices" element={<ParentNotices />} />
        <Route path="parent-learning" element={<ParentLearning />} />
        <Route path="teacher-lms" element={<TeacherLMS />} />
        <Route path="admin-lms" element={<AdminLMS />} />
        <Route path="certificate/:badgeId" element={<CertificateView />} />
        <Route path="refer" element={<ParentReferAFriend />} />
        <Route path="my-settings" element={<ParentSettings />} />
        <Route path="profile" element={<StaffSettings />} />
        {/* Universal profile / account security — accessible to every role */}
        <Route path="profile/security" element={<ProfileSecurity />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}

function RouteLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-paper">
      <div className="flex flex-col items-center gap-3" data-testid="route-loader">
        <img src="/mangocubs-logo.png?v=20260514" alt="MangoCubs" width={48} height={48}
          className="animate-pulse" style={{ objectFit: "contain" }} />
        <div className="text-xs text-ink-soft uppercase tracking-wider">Loading…</div>
      </div>
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <BrandingProvider>
            <GlobalRouter />
            <FloatingWhatsApp />
            <Toaster position="top-right" richColors closeButton />
          </BrandingProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
